import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Card, Button, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import useShipments from "../../hooks/useShipments";
import { selectCurrentUser, selectActiveTin } from "../../redux/slices/authSlice";
import ShipmentForm from "../../components/shipments/ShipmentForm";
import ShipmentCreatedDialog from "../../components/shipments/ShipmentCreatedDialog";

// The single-shipment create form, as its own page (/dashboard/shipment/add)
// instead of a modal — same ShipmentForm component, same fields,
// same validation, same create logic as before (see CustomerShipments.jsx's
// now-removed inline create dialog, which this mirrors exactly); only the
// container changed from a Dialog to a page via ShipmentForm's
// `asPage` prop. Editing an existing shipment is untouched and still opens
// as a dialog from the shipments list.
const CustomerAddShipment = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector(selectCurrentUser);
  const activeTin = useSelector(selectActiveTin);
  const { create } = useShipments();

  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState(null);
  // Bumped on "Create Another" to force ShipmentForm to fully
  // remount — the simplest way to get a genuinely blank form again
  // without touching that component's own internal reset logic.
  const [formKey, setFormKey] = useState(0);

  const hasTin = user?.canManageMultipleTins ? (user?.tins?.length || 0) > 0 : !!user?.tin;

  const handleCreate = async (data, filesMeta) => {
    setCreating(true);
    try {
      const result = await create(data);
      if (result) {
        if (filesMeta?.pendingFiles?.length > 0) {
          try {
            const shipment = result;
            let itemId;
            if (filesMeta.attachTo && filesMeta.attachTo.startsWith("item:")) {
              const idx = parseInt(filesMeta.attachTo.split(":")[1], 10);
              itemId = shipment.items?.[idx]?._id;
            }
            await import("../../services/documentService").then((mod) =>
              mod.default.upload(shipment._id, filesMeta.pendingFiles, filesMeta.docType, undefined, itemId),
            );
          } catch (err) {
            // ignore upload errors here; user can upload later from Upload page
          }
        }
        setJustCreated(result);
      }
    } finally {
      setCreating(false);
    }
  };

  if (!hasTin) {
    enqueueSnackbar("Please add your TIN to your profile before creating a shipment", { variant: "warning" });
    navigate("/dashboard/profile", { replace: true });
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/dashboard/shipments"))}
          variant="outlined"
          size="small"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            New Shipment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Log a new order for us to receive and ship
          </Typography>
        </Box>
      </Box>

      <Card sx={{ p: { xs: 1.5, sm: 3 } }}>
        <ShipmentForm
          key={formKey}
          asPage
          open
          onClose={() => navigate("/dashboard/shipments")}
          onSubmit={handleCreate}
          loading={creating}
          currentUser={user}
          activeTin={activeTin}
        />
      </Card>

      <ShipmentCreatedDialog
        open={!!justCreated}
        trackingNumber={justCreated?.trackingNumber}
        onClose={() => navigate("/dashboard/shipments")}
        onCreateAnother={() => {
          setJustCreated(null);
          setFormKey((k) => k + 1);
        }}
      />
    </Box>
  );
};

export default CustomerAddShipment;
